/**
 * RequireActiveSubscription Guard
 * ================================
 * 
 * DELTA WORK ORDER 2026-02-08: Subscription Gating + Reactivation
 * UPDATED 2026-02-14: Bootstrap-pending state handling
 * 
 * Route guard that ensures user has an active subscription per
 * billingCustomers/{uid}.subscriptionStatus truth source.
 * 
 * Key Features:
 * - No redirect loops (explicit allowlist checking)
 * - Stores returnTo for post-payment redirect
 * - Admin/audit bypasses for testing
 * - Waits for bootstrap completion before checking subscription
 * 
 * Usage:
 * ```tsx
 * <RequireActiveSubscription redirectTo="/pro/billing">
 *   <ProFeaturePage />
 * </RequireActiveSubscription>
 * ```
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { FORCE_ACTIVE_SUBSCRIPTION } from '@/config/devOverrides';
import { useAuth } from '@/auth/AuthProvider';
import { isEmulator } from '@/lib/environment';
import { Loader2 } from 'lucide-react';

interface RequireActiveSubscriptionProps {
  children?: React.ReactNode;
  /** Where to redirect if subscription is inactive */
  redirectTo?: string;
  /** Optional message to display */
  message?: string;
  /** Additional routes that should be accessible without subscription */
  additionalAllowlist?: string[];
}

/**
 * Routes that are ALWAYS accessible regardless of subscription status.
 * CRITICAL: These routes must NOT trigger redirects or infinite loops occur.
 */
const DEFAULT_ALLOWLIST = new Set([
  '/billing',
  '/settings/billing',
  '/pro/billing',
  '/subscribe',
  '/pro/subscribe',
  '/subscribe/success',
  '/pro/subscribe/success',
  '/logout',
  '/profile',
  '/settings',
]);

/**
 * Check if current path is on the allowlist (exact match or prefix match)
 */
function isAllowlistedPath(pathname: string, additionalPaths: string[] = []): boolean {
  // Exact match
  if (DEFAULT_ALLOWLIST.has(pathname)) {
    return true;
  }
  
  // Additional allowlist
  if (additionalPaths.some(p => pathname === p || pathname.startsWith(p + '/'))) {
    return true;
  }
  
  // Prefix match (for nested routes like /subscribe/*)
  for (const allowedPath of DEFAULT_ALLOWLIST) {
    if (pathname.startsWith(allowedPath + '/') || pathname.startsWith(allowedPath + '?')) {
      return true;
    }
  }
  
  return false;
}

export default function RequireActiveSubscription({
  children,
  redirectTo = '/settings/billing',
  message = 'Active subscription required',
  additionalAllowlist = [],
}: RequireActiveSubscriptionProps) {
  const location = useLocation();
  const { isActive, loading, isPastDue } = useSubscriptionStatus();
  const { user, claims, loading: authLoading } = useAuth();

  // DEBUG: Log guard state in DEV
  if (import.meta.env.DEV) {
    console.log('[RequireActiveSubscription] Guard state:', {
      pathname: location.pathname,
      isActive,
      loading,
      isPastDue,
      claims: { admin: claims?.admin, role: claims?.role, auditBypass: claims?.auditBypassBilling },
    });
  }

  // CRITICAL: Prevent redirect loops - if already on allowlisted route, do nothing
  if (isAllowlistedPath(location.pathname, additionalAllowlist)) {
    if (import.meta.env.DEV) {
      console.info('[RequireActiveSubscription] On allowlisted route, rendering directly:', location.pathname);
    }
    return <>{children || <Outlet />}</>;
  }

  // AUDIT BYPASS: Emulator + auditBypassBilling claim
  // This restores observability in test environment (emulators)
  // Fails closed: requires BOTH emulator AND explicit claim
  const isEmulatorEnv = isEmulator();
  const hasAuditBypass = claims?.auditBypassBilling === true;
  const auditBypass = isEmulatorEnv && hasAuditBypass;

  if (auditBypass) {
    console.info('[RequireActiveSubscription] ✅ AUDIT BYPASS: Emulator + auditBypassBilling claim', {
      isEmulator: isEmulatorEnv,
      auditBypassBilling: hasAuditBypass,
    });
    return <>{children || <Outlet />}</>;
  }

  // Admin users (super/support) bypass the subscription requirement in all environments.
  // Admin accounts are internal platform accounts; they are never billed as Pro subscribers.
  // Note: previously this was DEV-only, which caused production admins to be redirected
  // to the Pro billing page when they navigated to subscription-gated routes.
  const isSuper = claims?.role === 'super';
  const isSupportAdmin = claims?.role === 'support';
  const isAdmin = claims?.admin === true;
  const adminBypass = isSuper || isSupportAdmin || isAdmin;

  if (adminBypass) {
    if (import.meta.env.DEV) {
      console.info('[RequireActiveSubscription] Admin bypass active', {
        role: claims?.role,
        admin: claims?.admin,
      });
    }
    return <>{children || <Outlet />}</>;
  }

  // Demo Pro bypass: Allow demo professionals to access without subscription
  // Enables testing of client onboarding workflows (Scenarios 2-3)
  const isDemoPro = claims?.demo === true && claims?.isPro === true;
  if (isDemoPro) {
    if (import.meta.env.DEV) {
      console.info('[RequireActiveSubscription] Demo Pro bypass active', {
        demo: claims?.demo,
        isPro: claims?.isPro,
      });
    }
    return <>{children || <Outlet />}</>;
  }

  // Wait for auth and subscription status
  if (authLoading || loading) {
    if (import.meta.env.DEV) {
      console.info('[RequireActiveSubscription] Loading...', {
        authLoading,
        subscriptionLoading: loading,
      });
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="animate-spin" size={20} />
          Checking subscription status...
        </div>
      </div>
    );
  }

  // Bootstrap pending check: If user exists but claims are not yet loaded,
  // wait for claims to be fetched before making subscription decisions
  // claims will be undefined until getIdTokenResult() completes in AuthProvider
  const isBootstrapPending = 
    user && 
    !authLoading &&
    claims === undefined; // Claims not loaded yet (vs loaded with falsy values)

  if (isBootstrapPending) {
    console.log('[RequireActiveSubscription] Bootstrap pending, waiting for claims...');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 className="animate-spin" size={20} />
          Completing sign in...
        </div>
      </div>
    );
  }

  // If not authenticated, let RequireAuth handle it
  if (!user) {
    if (import.meta.env.DEV) {
      console.info('[RequireActiveSubscription] No user, deferring to RequireAuth');
    }
    return <>{children || <Outlet />}</>;
  }

  // Redirect if subscription is not active
  if (!isActive && !FORCE_ACTIVE_SUBSCRIPTION) {
    const currentPath = location.pathname + location.search;
    
    if (import.meta.env.DEV) {
      console.info('[RequireActiveSubscription] Subscription inactive, redirecting...', {
        isActive,
        isPastDue,
        redirectTo,
        from: currentPath,
        devModeBypass: FORCE_ACTIVE_SUBSCRIPTION,
      });
    }

    // Store intended destination for post-payment redirect
    // Use location.state.returnTo if available (from previous redirect), otherwise current path
    const returnTo = (location.state as Record<string, unknown>)?.returnTo as string | undefined || currentPath;
    sessionStorage.setItem('billing_redirect_after_payment', returnTo);

    return (
      <Navigate 
        to={redirectTo} 
        state={{ 
          message,
          isPastDue,
          returnTo,
          from: currentPath,
        }} 
        replace 
      />
    );
  }

  // Subscription is active, render children
  if (import.meta.env.DEV) {
    console.info('[RequireActiveSubscription] Access granted', {
      isActive,
      isPastDue,
      devModeBypass: FORCE_ACTIVE_SUBSCRIPTION,
    });
  }

  return <>{children || <Outlet />}</>;
}
